import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import type { HubPresenceAvatarPerson } from "@/components/HubPresenceAvatarStack";
import {
  fetchHubPresencePageEntities,
  hubPresencePageEntitiesQueryKey,
} from "@/lib/hubPresenceApi";
import { useHubPagePresenceStream } from "@/lib/useHubPagePresenceStream";

/** Per-queue-item presence for pages that select rows via ?request= (entity type request). */
export function useQueueItemPresence(input: {
  pathname: string;
  entityType?: string;
  enabled?: boolean;
}): {
  viewersForEntity: (entityId: string) => HubPresenceAvatarPerson[];
} {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const viewerUserId = session?.user?.id ?? null;
  const entityType = input.entityType ?? "request";
  const enabled =
    input.enabled !== false
    && viewerUserId != null
    && viewerUserId !== ""
    && input.pathname.trim() !== "";

  const queryKey = hubPresencePageEntitiesQueryKey(
    viewerUserId ?? "unauthenticated",
    input.pathname,
    entityType,
  );

  const presenceQuery = useQuery({
    queryKey,
    queryFn: () => fetchHubPresencePageEntities(input.pathname, entityType),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const refresh = useCallback(() => {
    if (!enabled) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey });
  }, [enabled, queryClient, queryKey]);

  useHubPagePresenceStream({
    scope: "global",
    enabled,
    connectionKey: `queue-item-presence:${input.pathname}:${entityType}`,
    onPresenceChange: refresh,
  });

  const entities = presenceQuery.data?.entities ?? {};

  const viewersForEntity = useMemo(() => {
    const map = new Map<string, HubPresenceAvatarPerson[]>();
    for (const [entityId, viewers] of Object.entries(entities)) {
      map.set(
        entityId,
        viewers.map((viewer) => ({
          userId: viewer.userId,
          email: viewer.email,
          displayName: viewer.displayName,
          avatarUrl: viewer.avatarUrl,
          presenceState: viewer.presenceState,
          tabCount: viewer.tabCount,
        })),
      );
    }
    return (entityId: string): HubPresenceAvatarPerson[] => map.get(entityId) ?? [];
  }, [entities]);

  return { viewersForEntity };
}
